import {
  Client,
  ChargeType,
  ClassificationGroup,
  ClientRateCardInput,
} from '@core/generated/gql-types';
import { FixedRatesTable, type FixedRatesTableProps } from './FixedRatesTable';
import { MarginRatesTable } from './MarginRatesTable';
import { RateCardRates } from './RateCardRates';

export interface ClientRatesBodyProps {
  chargeType: ChargeType;
  client: Client;
  classificationGroups: ClassificationGroup[];
  tenantMarginPercentage: number | null;
  clientRates: FixedRatesTableProps['clientRates'];
  isLoadingFixedRates: boolean;
  onSaveFixedRate: FixedRatesTableProps['onSave'];
  onSaveClientRateCard: (
    rateCard: ClientRateCardInput,
  ) => Promise<{ success: boolean; error?: string }>;
  isRatesEditingDisabled: boolean;
}

export const ClientRatesBody = ({
  chargeType,
  client,
  classificationGroups,
  tenantMarginPercentage,
  clientRates,
  isLoadingFixedRates,
  onSaveFixedRate,
  onSaveClientRateCard,
  isRatesEditingDisabled,
}: ClientRatesBodyProps) => {
  switch (chargeType) {
    case ChargeType.Margin: {
      const marginIntro = (
        <p className="mb-2 text-sm text-gray-600">
          Margin rates are calculated from classification level minimums +
          margin percentage. Read-only.
        </p>
      );
      if (classificationGroups.length === 0) {
        return (
          <>
            {marginIntro}
            <p className="text-gray-500">No classification groups found.</p>
          </>
        );
      }
      return (
        <>
          {marginIntro}
          <MarginRatesTable
            client={client}
            classificationGroups={classificationGroups}
            tenantMarginPercentage={tenantMarginPercentage}
          />
        </>
      );
    }
    case ChargeType.Fixed: {
      const fixedIntro = (
        <p className="mb-2 text-sm text-gray-600">
          Fixed rates are client-specific. Add and edit rates per classification
          level and country.
        </p>
      );
      if (isLoadingFixedRates) {
        return (
          <>
            {fixedIntro}
            <p className="text-gray-500">Loading fixed rates...</p>
          </>
        );
      }
      if (classificationGroups.length === 0) {
        return (
          <>
            {fixedIntro}
            <p className="text-gray-500">No classification groups found.</p>
          </>
        );
      }
      return (
        <>
          {fixedIntro}
          <FixedRatesTable
            classificationGroups={classificationGroups}
            clientRates={clientRates}
            onSave={onSaveFixedRate}
            disabled={isRatesEditingDisabled}
          />
        </>
      );
    }
    case ChargeType.RateCard:
    default:
      return (
        <>
          <p className="mb-2 text-sm text-gray-600">
            Rate card defines how this client is charged by job length (calendar
            days). Use flat fees or a daily rate per tier.
          </p>
          <RateCardRates
            client={client}
            disabled={isRatesEditingDisabled}
            onSave={onSaveClientRateCard}
          />
        </>
      );
  }
};
